import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useSiteContent } from '../context/SiteContentContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const { toast } = useToast();
  const { contact: c } = useSiteContent();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/contact/`, formData);
      toast({ title: 'Message Sent!', description: "We'll get back to you within 24 hours." });
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (err) {
      toast({ title: 'Could not send', description: err.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    }
  };
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const info = [
    { icon: MapPin, title: 'Our Office', value: c.office },
    { icon: Phone, title: 'Phone', value: c.phone },
    { icon: Mail, title: 'Email', value: c.email },
    { icon: Clock, title: 'Working Hours', value: c.hours }
  ];

  return (
    <div className="bg-white" data-testid="contact-page">
      <section className="bg-gradient-to-r from-[#003366] to-[#004080] text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fadeInUp font-heading">{c.hero.title}</h1>
          <p className="text-xl max-w-3xl mx-auto animate-fadeInUp">{c.hero.subtext}</p>
        </div>
      </section>

      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            <Card className="shadow-xl">
              <CardHeader>
                <CardTitle className="text-3xl text-gray-900">{c.formHeading}</CardTitle>
                <CardDescription className="text-lg">{c.formSubtext}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div><Label htmlFor="name">Full Name *</Label><Input id="name" name="name" value={formData.name} onChange={handleChange} required placeholder="Your name" className="mt-2" /></div>
                  <div><Label htmlFor="email">Email Address *</Label><Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required placeholder="your.email@example.com" className="mt-2" /></div>
                  <div><Label htmlFor="phone">Phone Number</Label><Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="+91 1234567890" className="mt-2" /></div>
                  <div><Label htmlFor="subject">Subject *</Label><Input id="subject" name="subject" value={formData.subject} onChange={handleChange} required placeholder="What is this regarding?" className="mt-2" /></div>
                  <div><Label htmlFor="message">Message *</Label><Textarea id="message" name="message" value={formData.message} onChange={handleChange} required placeholder="Tell us more..." className="mt-2 min-h-[150px]" /></div>
                  <Button type="submit" className="w-full bg-[#e74c3c] hover:bg-[#c0392b]" size="lg">Send Message</Button>
                </form>
              </CardContent>
            </Card>

            <div className="space-y-8">
              <div>
                <h2 className="text-3xl font-bold mb-6 text-[#003366] font-heading">{c.infoHeading}</h2>
                <p className="text-lg text-gray-700 mb-6">{c.infoText}</p>
              </div>
              <div className="space-y-6">
                {info.map((item) => (
                  <Card key={item.title} className="hover-lift">
                    <CardContent className="flex items-start gap-4 pt-6">
                      <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <item.icon className="w-6 h-6 text-[#e74c3c]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg mb-1 text-gray-900">{item.title}</h3>
                        <p className="text-gray-600 whitespace-pre-line">{item.value}</p>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {c.mapImage ? (
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <img src={c.mapImage} alt="Our location" className="rounded-lg shadow-xl w-full max-h-[420px] object-cover" loading="lazy" decoding="async" />
          </div>
        </section>
      ) : null}
    </div>
  );
};

export default Contact;
